/**
 * Session state.
 *
 * An external store rather than React context state, because route guards run
 * OUTSIDE the component tree and need a synchronous read. A guard that had to
 * await a render cycle could let one frame of the wrong screen through.
 *
 * Every mutation goes through `setSession` / `resetSession`, so subscribers
 * cannot observe a partially-applied state.
 */
import { useEffect, useState } from 'react';
import { onAuthEvent } from '@/api/client/auth-events';
import type { SessionListener, SessionSnapshot, SessionUser } from '@/app/session/types';

const INITIAL: SessionSnapshot = { status: 'loading', user: null, expiresAt: null };

let snapshot: SessionSnapshot = INITIAL;
let inFlight: Promise<SessionSnapshot> | null = null;
const listeners = new Set<SessionListener>();

/**
 * Supplies the current session.
 *
 * Injected rather than imported so this module never depends on the transport
 * layer, and so tests can supply a deterministic session without mocking fetch.
 * The real implementation is wired in `src/app/session/index.ts`.
 */
export type SessionLoader = () => Promise<{ user: SessionUser | null; expiresAt: number | null }>;

let loader: SessionLoader = async () => ({ user: null, expiresAt: null });

export function configureSessionLoader(next: SessionLoader): void {
  loader = next;
}

function publish(next: SessionSnapshot): void {
  snapshot = next;
  for (const listener of listeners) {
    listener(snapshot);
  }
}

export function getSessionSnapshot(): SessionSnapshot {
  return snapshot;
}

export function subscribeSession(listener: SessionListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function setSessionUser(user: SessionUser | null, expiresAt: number | null = null): void {
  publish({
    status: user ? 'authenticated' : 'anonymous',
    user,
    expiresAt,
  });
}

/**
 * Resolves the session once and caches the in-flight promise.
 *
 * Concurrent callers MUST share one request. Two guards resolving independently
 * would race, and the slower response could publish a stale session over a newer
 * one — a real cross-tenant risk after a workspace switch.
 */
export async function bootstrapSession(mode: 'quiet' | 'full' = 'full'): Promise<SessionSnapshot> {
  if (mode === 'quiet' && snapshot.status === 'authenticated') {
    return snapshot;
  }
  if (inFlight) return inFlight;

  inFlight = (async () => {
    try {
      const { user, expiresAt } = await loader();
      publish({ status: user ? 'authenticated' : 'anonymous', user, expiresAt });
      return snapshot;
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}

/** Clears local session state without contacting the server. */
export function resetSession(): void {
  publish(INITIAL);
}

/**
 * A transport-level 401 or an explicit sign-out clears local state immediately.
 *
 * Without this, a revoked session would keep rendering its last good screen
 * until the next navigation. The server has already refused the request, so the
 * correct response is to stop acting as though the user is signed in.
 */
onAuthEvent((event) => {
  if (event === 'unauthenticated' || event === 'signed-out' || event === 'refresh-failed') {
    resetSession();
  }
});

/** Test-only. Leaked listeners cause order-dependent failures. */
export function resetSessionTestState(): void {
  snapshot = INITIAL;
  inFlight = null;
  listeners.clear();
  loader = async () => ({ user: null, expiresAt: null });
}

/**
 * React binding for the session store.
 *
 * Re-renders when the session snapshot changes. Guards run outside the tree
 * and use `getSessionSnapshot` directly; components use this hook so they
 * re-render on sign-in, sign-out, and session expiry.
 */
export function useSession(): SessionSnapshot {
  const [snapshotState, setSnapshotState] = useState<SessionSnapshot>(getSessionSnapshot);

  useEffect(() => {
    const unsubscribe = subscribeSession((next) => {
      setSnapshotState(next);
    });
    return unsubscribe;
  }, []);

  return snapshotState;
}